/**
 * Preferred coding-agent host for SpecTree's host-transparent UX (migration A14).
 *
 * SpecTree supports two first-class, co-equal CLI hosts — GitHub Copilot CLI and
 * Claude Code. A user's interaction with SpecTree does not change based on host;
 * the product persists this preference and emits host-correct commands so the
 * steps and mental model stay identical (only the literal command string differs,
 * e.g. Copilot `@request-formulator` vs Claude `/spectree:request-formulator`).
 *
 * This is the single source of truth for the host value set; the API persists it,
 * the web app reads it, and Sprout consumes it to emit the matching command set.
 */
/** The supported host identifiers, in display order. */
export const PREFERRED_HOSTS = ["copilot", "claude"];
/**
 * Effective host used when a user has not chosen one (stored value is null).
 *
 * Defaults to GitHub Copilot CLI — the primary, recommended host for SpecTree.
 * Users can change this in settings/onboarding; choosing `claude` switches to
 * the Claude Code command set.
 */
export const DEFAULT_PREFERRED_HOST = "copilot";
/** Human-readable labels for each host, for settings/onboarding UI. */
export const PREFERRED_HOST_LABELS = {
    copilot: "GitHub Copilot CLI",
    claude: "Claude Code",
};
/** Narrow an unknown value to a valid {@link PreferredHost}. */
export function isPreferredHost(value) {
    return (typeof value === "string" && PREFERRED_HOSTS.includes(value));
}
/**
 * Resolve a possibly-null/unknown stored preference to an effective host,
 * falling back to {@link DEFAULT_PREFERRED_HOST} when unset or invalid.
 */
export function resolvePreferredHost(value) {
    return isPreferredHost(value) ? value : DEFAULT_PREFERRED_HOST;
}
/**
 * Execution-host attribution vocabulary (AiSession host/hostSource columns).
 *
 * `ExecutionHost` is deliberately NOT a reuse of {@link PreferredHost}. The two
 * types model different things:
 *   - `PreferredHost` is a user's *stated preference* for which CLI host to use
 *     going forward (persisted setting, defaults to `"copilot"`).
 *   - `ExecutionHost` is a *detected runtime fact*: which host actually ran a
 *     given AiSession, as observed by the orchestrator/MCP client/install
 *     config at execution time. It can diverge from the user's preference
 *     (e.g. a user prefers Claude but a session executes under Copilot), and
 *     unlike `PreferredHost` it must represent the "we don't know" case
 *     explicitly via `"unknown"` rather than falling back to a default.
 * Conflating the two would silently misattribute sessions whenever a user's
 * preference and actual execution host disagree, so they are kept separate.
 */
/** The supported execution-host identifiers actually observed at runtime. */
export const EXECUTION_HOSTS = ["claude", "copilot", "unknown"];
/** Narrow an unknown value to a valid {@link ExecutionHost}. */
export function isExecutionHost(value) {
    return (typeof value === "string" && EXECUTION_HOSTS.includes(value));
}
/**
 * How the {@link ExecutionHost} value for an AiSession was determined.
 *
 * - `orchestrator`: the orchestrator agent recorded the host it spawned workers on.
 * - `mcp-client-identity`: derived from the connecting MCP client's identity.
 * - `install-config`: derived from local install/config state (e.g. `.mcp.json`).
 * - `manual`: manually set (e.g. by an admin or a migration/backfill script).
 */
export const HOST_SOURCES = [
    "orchestrator",
    "mcp-client-identity",
    "install-config",
    "manual",
];
/** Narrow an unknown value to a valid {@link HostSource}. */
export function isHostSource(value) {
    return (typeof value === "string" && HOST_SOURCES.includes(value));
}
//# sourceMappingURL=host.js.map